export function kebabCase(str: string) {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '-');
}

// Used for schema keys where the usual AIP doesn't matter
export function kebabSnakeOrCamelCase(str: string) {
  return str.replace(/[^a-zA-Z0-9_]/g, '-');
}

export const isValidEmail = (email: string): boolean => {
  return !!String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

// Converts a name to a valid AIP-122 resource ID:
// 1-63 chars, pattern [a-z]([a-z0-9-]{0,61}[a-z0-9])?, lowercase letters/digits/hyphens only.
export const slugify = (name: string): string => {
  // Convert to lowercase and replace spaces/underscores with hyphens
  let id = name
    .toLowerCase()
    .trim()
    .replaceAll(/[\s_]+/g, '-');

  // Remove all characters that aren't lowercase letters, digits, or hyphens
  id = id.replaceAll(/[^a-z0-9-]/g, '');

  // Remove consecutive hyphens
  id = id.replaceAll(/-+/g, '-');

  // Ensure it starts with a letter
  id = id.replace(/^[^a-z]+/, '');

  // Ensure it ends with a letter or digit (remove trailing hyphens)
  id = id.replace(/-+$/, '');

  // Limit to 63 characters
  if (id.length > 63) {
    id = id.substring(0, 63);
    // Re-trim trailing hyphens after truncation
    id = id.replace(/-+$/, '');
  }

  return id;
};

export const capitalize = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const humanize = (str: string): string =>
  capitalize(str.replace(/[_-]/g, ' '));

export const countWords = (str: string): number => {
  return str.trim().split(/\s+/).filter(Boolean).length;
};

export const pluralize = (
  count: number,
  singular: string,
  plural?: string,
): string => (count === 1 ? singular : (plural ?? `${singular}s`));

export const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const ensureLeadingSlash = (path: string): string =>
  path.startsWith('/') ? path : `/${path}`;

export const formatTruncatedList = (
  items: string[],
  max: number = 3,
): string => {
  const listed = items.slice(0, max).join(', ');
  const remaining = items.length - max;
  return remaining > 0 ? `${listed} and ${remaining} more` : listed;
};

const andListFormatter = new Intl.ListFormat('en', {
  style: 'long',
  type: 'conjunction',
});

export const andList = (items: Iterable<string>): string =>
  andListFormatter.format(items);

const orListFormatter = new Intl.ListFormat('en', {
  style: 'long',
  type: 'disjunction',
});

export const orList = (items: Iterable<string>): string =>
  orListFormatter.format(items);
